import { useCallback, useEffect, useRef, useState } from 'react';

import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Popover,
} from '@mui/material';
import type Quill from 'quill';

import { VarBlot } from './quillBlots';
import {
  type ActiveTrigger,
  replaceTriggerWithSuggestion,
} from './replaceTriggerWithSuggestion';
import type { EmailEditorSuggestion } from './types';

const MAX_RESULTS = 20;

const TRIGGER_RE = /#([\w._-]*)$/;

type AnchorPosition = {
  top: number;
  left: number;
};

const detectTrigger = (
  textBeforeCaret: string,
  caretIndex: number,
): ActiveTrigger | null => {
  const match = TRIGGER_RE.exec(textBeforeCaret);
  if (
    match == null ||
    (match.index > 0 && !/\s/.test(textBeforeCaret[match.index - 1]))
  ) {
    return null;
  }
  const query = match[1];
  return { triggerIndex: caretIndex - (query.length + 1), query };
};

const filterSuggestions = (
  suggestions: readonly EmailEditorSuggestion[],
  query: string,
): EmailEditorSuggestion[] => {
  const search = query.toLowerCase().trim();
  return suggestions
    .filter(
      suggestion =>
        // `name` is what the inserted chip displays (see `select` below), so an
        // entry without one inserts an invisible chip and ships an empty span in
        // the email. In resolved contexts (composer, signature) `name` is the
        // resolved value and can legitimately be empty: the contact has no first
        // name, the agent no phone. Offering nothing beats offering nothing
        // visible. The template editor is unaffected, its `name` is the marker
        // itself, stored verbatim and resolved at send time.
        suggestion.name.trim() !== '' &&
        (search.length === 0 ||
          suggestion.id.toLowerCase().includes(search) ||
          suggestion.name.toLowerCase().includes(search)),
    )
    .slice(0, MAX_RESULTS);
};

export const MentionDropdown = ({
  quill,
  suggestions,
}: {
  quill: Quill | null;
  suggestions: readonly EmailEditorSuggestion[];
}) => {
  const [active, setActive] = useState<ActiveTrigger | null>(null);
  const [anchorPosition, setAnchorPosition] = useState<AnchorPosition | null>(
    null,
  );
  const [highlighted, setHighlighted] = useState(0);

  const matches =
    active != null ? filterSuggestions(suggestions, active.query) : [];
  const open = active != null && matches.length > 0;

  const matchesRef = useRef(matches);
  matchesRef.current = matches;
  const highlightedRef = useRef(highlighted);
  highlightedRef.current = highlighted;
  const activeRef = useRef(active);
  activeRef.current = active;

  const closeDropdown = useCallback(() => {
    setActive(null);
    setAnchorPosition(null);
    setHighlighted(0);
  }, []);

  const select = useCallback(
    (suggestion: EmailEditorSuggestion) => {
      const current = activeRef.current;
      if (quill == null || current == null) {
        return;
      }
      const caretIndex = quill.getSelection()?.index ?? quill.getLength();
      const nextIndex = replaceTriggerWithSuggestion(
        quill,
        current,
        suggestion,
        caretIndex,
        VarBlot.blotName,
      );
      quill.setSelection(nextIndex, 0, 'user');
      closeDropdown();
    },
    [quill, closeDropdown],
  );

  const selectRef = useRef(select);
  selectRef.current = select;

  useEffect(() => {
    if (quill == null) {
      return;
    }
    const update = () => {
      const range = quill.getSelection();
      if (range == null || range.length > 0) {
        closeDropdown();
        return;
      }
      const textBeforeCaret = quill.getText(0, range.index);
      const trigger = detectTrigger(textBeforeCaret, range.index);
      if (trigger == null) {
        closeDropdown();
        return;
      }
      const bounds = quill.getBounds(trigger.triggerIndex);
      const rootRect = quill.root.getBoundingClientRect();
      if (bounds != null) {
        setAnchorPosition({
          top: rootRect.top + bounds.bottom,
          left: rootRect.left + bounds.left,
        });
      }
      setActive(prev => {
        if (prev == null || prev.triggerIndex !== trigger.triggerIndex) {
          setHighlighted(0);
        }
        return trigger;
      });
    };

    quill.on('editor-change', update);
    return () => {
      quill.off('editor-change', update);
    };
  }, [quill, closeDropdown]);

  useEffect(() => {
    if (highlighted > matches.length - 1) {
      setHighlighted(Math.max(0, matches.length - 1));
    }
  }, [highlighted, matches.length]);

  useEffect(() => {
    if (quill == null) {
      return;
    }
    const root = quill.root;
    const onKeyDown = (event: KeyboardEvent) => {
      if (activeRef.current == null || matchesRef.current.length === 0) {
        if (event.key === 'Escape' && activeRef.current != null) {
          closeDropdown();
        }
        return;
      }
      const count = matchesRef.current.length;
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          event.stopPropagation();
          setHighlighted(prev => (prev + 1) % count);
          break;
        case 'ArrowUp':
          event.preventDefault();
          event.stopPropagation();
          setHighlighted(prev => (prev - 1 + count) % count);
          break;
        case 'Enter': {
          event.preventDefault();
          event.stopPropagation();
          const chosen = matchesRef.current[highlightedRef.current];
          if (chosen != null) {
            selectRef.current(chosen);
          }
          break;
        }
        case 'Escape':
          event.preventDefault();
          event.stopPropagation();
          closeDropdown();
          break;
        default:
          break;
      }
    };
    root.addEventListener('keydown', onKeyDown, true);
    return () => {
      root.removeEventListener('keydown', onKeyDown, true);
    };
  }, [quill, closeDropdown]);

  if (!open || anchorPosition == null) {
    return null;
  }

  return (
    <Popover
      open
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      onClose={closeDropdown}
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <Paper sx={{ width: 320, maxHeight: 250, overflowY: 'auto' }}>
        <List dense disablePadding data-testid="mention-dropdown">
          {matches.map((suggestion, index) => (
            <ListItem key={suggestion.id} disablePadding>
              <ListItemButton
                selected={index === highlighted}
                onMouseDown={event => {
                  event.preventDefault();
                }}
                onClick={() => select(suggestion)}
              >
                <ListItemText
                  primary={suggestion.name}
                  secondary={
                    suggestion.id !== suggestion.name
                      ? suggestion.id
                      : undefined
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Paper>
    </Popover>
  );
};
